import { getSupabaseClient } from './supabase';

/**
 * Warehouse library data (docs/warehouse.md): every list is searched, filtered, grouped, sorted
 * and paged on the server, with exact counts, folds and hero stats in the same response.
 */

export type WarehouseRef = { id: string; name: string | null };
export type WarehouseDocRef = { id: string; docNo: string | null };
export type WarehouseEventRef = { id: string; code: string | null; name: string | null };

export type WarehouseListGroup = {
  key: string;
  label: string | null;
  count: number;
  fromDay: string | null;
  toDay: string | null;
};

export type WarehouseListFold = {
  key: string;
  count: number;
  olderThanYear: number;
  oldestDay: string | null;
  included: boolean;
};

export type WarehouseCounts = { [key: string]: number };

export type WarehouseListPage<Row> = {
  rows: Row[];
  total: number;
  offset: number;
  limit: number;
  groups: WarehouseListGroup[];
  fold: WarehouseListFold | null;
  stats: WarehouseCounts;
  chipCounts: WarehouseCounts;
  asOf: string | null;
};

export type WarehouseTransferRow = {
  id: string;
  docNo: string | null;
  source: 'r2' | 'portal';
  status: string;
  description: string | null;
  fromSite: WarehouseRef | null;
  toSite: WarehouseRef | null;
  shipDay: string | null;
  arriveDay: string | null;
  shippedAt: string | null;
  receivedAt: string | null;
  daysLate: number | null;
  groupKey: string | null;
  scope: 'temporary' | 'permanent';
  returnDueDay: string | null;
  returnLeg: WarehouseDocRef | null;
  serviceTicket: WarehouseDocRef | null;
  lineCount: number;
  units: number;
  pickedUnits: number;
  shippedUnits: number;
  receivedUnits: number;
  firstItemName: string | null;
  event: WarehouseEventRef | null;
  eventCount: number;
  lastActivityAt: string | null;
};

export type WarehousePurchaseOrderRow = {
  id: string;
  docNo: string | null;
  source: 'r2' | 'portal';
  status: string;
  kind: 'goods' | 'labor';
  vendor: WarehouseRef | null;
  site: WarehouseRef | null;
  event: WarehouseEventRef | null;
  orderedDay: string | null;
  expectedDay: string | null;
  daysLate: number | null;
  groupKey: string | null;
  lineCount: number;
  unitsOrdered: number;
  unitsReceived: number;
  laborLineCount: number;
  laborLinesDone: number;
  totalAmount: number | null;
  r2SyncedAt: string | null;
  lastActivityAt: string | null;
};

export type WarehouseSubrentalRow = {
  id: string;
  recordKind: 'order' | 'subrental';
  docNo: string | null;
  source: 'r2' | 'portal';
  status: string;
  kind: 'event' | 'shared_stock' | 'vehicle';
  vendor: WarehouseRef | null;
  site: WarehouseRef | null;
  event: WarehouseEventRef | null;
  eventCount: number;
  unitsWithEvents: number;
  lineCount: number;
  itemSummary: string | null;
  windowStartDay: string | null;
  windowEndDay: string | null;
  expectedDay: string | null;
  daysLate: number | null;
  dueBackInDays: number | null;
  extended: boolean;
  unitsTotal: number;
  unitsIn: number;
  unitsReturned: number;
  costMode: 'order_total' | 'per_day';
  costAmount: number | null;
  costPerDay: number | null;
  costDays: number | null;
  costIsEstimate: boolean;
  groupKey: string | null;
  lastActivityAt: string | null;
};

export type WarehouseTabCounts = {
  transfers: number | null;
  requests: number | null;
  purchaseOrders: number | null;
  subrentals: number | null;
};

type Json = { [key: string]: unknown };

function asObject(value: unknown): Json | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Json : null;
}

function asText(value: unknown): string | null {
  if (value == null) return null;
  const text = String(value);
  return text.length > 0 ? text : null;
}

function asNumber(value: unknown, fallback = 0): number {
  const number = typeof value === 'number' ? value : Number(value ?? NaN);
  return Number.isFinite(number) ? number : fallback;
}

function asNullableNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) ? number : null;
}

function asRef(value: unknown): WarehouseRef | null {
  const object = asObject(value);
  const id = asText(object?.id);
  return id ? { id, name: asText(object?.name) } : null;
}

function asDocRef(value: unknown): WarehouseDocRef | null {
  const object = asObject(value);
  const id = asText(object?.id);
  return id ? { id, docNo: asText(object?.doc_no) } : null;
}

function asEventRef(value: unknown): WarehouseEventRef | null {
  const object = asObject(value);
  const id = asText(object?.id);
  return id ? { id, code: asText(object?.code), name: asText(object?.name) } : null;
}

function asSource(value: unknown): 'r2' | 'portal' {
  return value === 'r2' ? 'r2' : 'portal';
}

function asCounts(value: unknown): WarehouseCounts {
  const object = asObject(value);
  const counts: WarehouseCounts = {};
  if (!object) return counts;
  Object.entries(object).forEach(([key, raw]) => {
    const number = asNullableNumber(raw);
    if (number != null) counts[key] = number;
  });
  return counts;
}

function parsePage<Row>(data: unknown, parseRow: (row: Json) => Row | null): WarehouseListPage<Row> {
  const object = asObject(data) ?? {};
  const rows = (Array.isArray(object.rows) ? object.rows : [])
    .map((row) => asObject(row))
    .filter((row): row is Json => row != null)
    .map(parseRow)
    .filter((row): row is Row => row != null);
  const groups = (Array.isArray(object.groups) ? object.groups : [])
    .map((group) => asObject(group))
    .filter((group): group is Json => group != null)
    .map((group) => ({
      key: String(group.key ?? ''),
      label: asText(group.label),
      count: asNumber(group.count),
      fromDay: asText(group.from_day ?? group.from),
      toDay: asText(group.to_day ?? group.to),
    }))
    .filter((group) => group.key.length > 0);
  const foldObject = asObject(object.fold);
  return {
    rows,
    total: asNumber(object.total),
    offset: asNumber(object.offset),
    limit: asNumber(object.limit, 50),
    groups,
    fold: foldObject
      ? {
        key: String(foldObject.key ?? 'fold'),
        count: asNumber(foldObject.count),
        olderThanYear: asNumber(foldObject.older_than_year),
        oldestDay: asText(foldObject.oldest_day ?? foldObject.oldest_at)?.slice(0, 10) ?? null,
        included: foldObject.included === true,
      }
      : null,
    stats: asCounts(object.stats),
    chipCounts: asCounts(object.chip_counts),
    asOf: asText(object.as_of),
  };
}

function parseTransferRow(row: Json): WarehouseTransferRow | null {
  const id = asText(row.id);
  if (!id) return null;
  return {
    id,
    docNo: asText(row.doc_no),
    source: asSource(row.source),
    status: String(row.status ?? ''),
    description: asText(row.description),
    fromSite: asRef(row.from_site),
    toSite: asRef(row.to_site),
    shipDay: asText(row.ship_day),
    arriveDay: asText(row.arrive_day),
    shippedAt: asText(row.shipped_at),
    receivedAt: asText(row.received_at),
    daysLate: asNullableNumber(row.days_late),
    groupKey: asText(row.group_key),
    scope: row.scope === 'permanent' ? 'permanent' : 'temporary',
    returnDueDay: asText(row.return_due_day),
    returnLeg: asDocRef(row.return_leg),
    serviceTicket: asDocRef(row.service_ticket),
    lineCount: asNumber(row.line_count),
    units: asNumber(row.units),
    pickedUnits: asNumber(row.picked_units),
    shippedUnits: asNumber(row.shipped_units),
    receivedUnits: asNumber(row.received_units),
    firstItemName: asText(row.first_item_name),
    event: asEventRef(row.event),
    eventCount: asNumber(row.event_count),
    lastActivityAt: asText(row.last_activity_at),
  };
}

function parsePurchaseOrderRow(row: Json): WarehousePurchaseOrderRow | null {
  const id = asText(row.id);
  if (!id) return null;
  return {
    id,
    docNo: asText(row.doc_no),
    source: asSource(row.source),
    status: String(row.status ?? ''),
    kind: row.kind === 'labor' ? 'labor' : 'goods',
    vendor: asRef(row.vendor),
    site: asRef(row.site),
    event: asEventRef(row.event),
    orderedDay: asText(row.ordered_day),
    expectedDay: asText(row.expected_day),
    daysLate: asNullableNumber(row.days_late),
    groupKey: asText(row.group_key),
    lineCount: asNumber(row.line_count),
    unitsOrdered: asNumber(row.units_ordered),
    unitsReceived: asNumber(row.units_received),
    laborLineCount: asNumber(row.labor_line_count),
    laborLinesDone: asNumber(row.labor_lines_done),
    totalAmount: asNullableNumber(row.total_amount),
    r2SyncedAt: asText(row.r2_synced_at),
    lastActivityAt: asText(row.last_activity_at),
  };
}

function parseSubrentalRow(row: Json): WarehouseSubrentalRow | null {
  const id = asText(row.id);
  if (!id) return null;
  const kind = row.kind === 'vehicle' ? 'vehicle' : row.kind === 'event' ? 'event' : 'shared_stock';
  return {
    id,
    recordKind: row.record_kind === 'subrental' ? 'subrental' : 'order',
    docNo: asText(row.doc_no),
    source: asSource(row.source),
    status: String(row.status ?? ''),
    kind,
    vendor: asRef(row.vendor),
    site: asRef(row.site),
    event: asEventRef(row.event),
    eventCount: asNumber(row.event_count),
    unitsWithEvents: asNumber(row.units_with_events),
    lineCount: asNumber(row.line_count),
    itemSummary: asText(row.item_summary),
    windowStartDay: asText(row.window_start_day),
    windowEndDay: asText(row.window_end_day),
    expectedDay: asText(row.expected_day),
    daysLate: asNullableNumber(row.days_late),
    dueBackInDays: asNullableNumber(row.due_back_in_days),
    extended: row.extended === true,
    unitsTotal: asNumber(row.units_total),
    unitsIn: asNumber(row.units_in),
    unitsReturned: asNumber(row.units_returned),
    costMode: row.cost_mode === 'per_day' ? 'per_day' : 'order_total',
    costAmount: asNullableNumber(row.cost_amount),
    costPerDay: asNullableNumber(row.cost_per_day),
    costDays: asNullableNumber(row.cost_days),
    costIsEstimate: row.cost_is_estimate === true,
    groupKey: asText(row.group_key),
    lastActivityAt: asText(row.last_activity_at),
  };
}

/** Shared list parameters; tab-specific ones are added by each fetcher. */
export type WarehouseListParams = {
  search: string;
  locationIds: string[] | null;
  statuses: string[] | null;
  chips: string[];
  eventId: string | null;
  includeFold: boolean;
  groupBy: string;
  sortKey: string;
  sortDir: 'asc' | 'desc';
  offset: number;
  limit: number;
  timezone: string;
};

function commonArgs(params: WarehouseListParams) {
  return {
    p_search: params.search.trim() || null,
    p_location_ids: params.locationIds && params.locationIds.length > 0 ? params.locationIds : null,
    p_statuses: params.statuses && params.statuses.length > 0 ? params.statuses : null,
    p_chips: params.chips.length > 0 ? params.chips : null,
    p_event_id: params.eventId || null,
    p_include_fold: params.includeFold,
    p_group_by: params.groupBy,
    p_sort_key: params.sortKey,
    p_sort_dir: params.sortDir,
    p_offset: Math.max(0, params.offset),
    p_limit: Math.min(Math.max(1, params.limit), 100),
    p_timezone: params.timezone,
    p_as_of: null,
  };
}

function dateOrNull(value: string | null | undefined): string | null {
  return value && value.trim() ? value.trim() : null;
}

export type WarehouseTransfersParams = WarehouseListParams & {
  direction: 'both' | 'out' | 'in';
  shipFrom: string | null;
  shipTo: string | null;
  createdFrom: string | null;
  createdTo: string | null;
};

export async function fetchWarehouseTransfersPage(params: WarehouseTransfersParams): Promise<WarehouseListPage<WarehouseTransferRow>> {
  const { data, error } = await getSupabaseClient().rpc('get_warehouse_transfers_page', {
    ...commonArgs(params),
    p_direction: params.direction,
    p_ship_from: dateOrNull(params.shipFrom),
    p_ship_to: dateOrNull(params.shipTo),
    p_created_from: dateOrNull(params.createdFrom),
    p_created_to: dateOrNull(params.createdTo),
  });
  if (error) throw error;
  return parsePage(data, parseTransferRow);
}

export type WarehousePurchaseOrdersParams = WarehouseListParams & {
  kind: 'all' | 'goods' | 'labor';
  expectedFrom: string | null;
  expectedTo: string | null;
  orderedFrom: string | null;
  orderedTo: string | null;
  vendorIds: string[];
  orderedBy: string | null;
};

export async function fetchWarehousePurchaseOrdersPage(params: WarehousePurchaseOrdersParams): Promise<WarehouseListPage<WarehousePurchaseOrderRow>> {
  const { data, error } = await getSupabaseClient().rpc('get_warehouse_purchase_orders_page', {
    ...commonArgs(params),
    p_kind: params.kind,
    p_expected_from: dateOrNull(params.expectedFrom),
    p_expected_to: dateOrNull(params.expectedTo),
    p_ordered_from: dateOrNull(params.orderedFrom),
    p_ordered_to: dateOrNull(params.orderedTo),
    p_vendor_ids: params.vendorIds.length > 0 ? params.vendorIds : null,
    p_ordered_by: params.orderedBy || null,
  });
  if (error) throw error;
  return parsePage(data, parsePurchaseOrderRow);
}

export type WarehouseSubrentalsParams = WarehouseListParams & {
  kind: 'all' | 'equipment' | 'vehicles';
  windowFrom: string | null;
  windowTo: string | null;
  vendorIds: string[];
};

export async function fetchWarehouseSubrentalsPage(params: WarehouseSubrentalsParams): Promise<WarehouseListPage<WarehouseSubrentalRow>> {
  const { data, error } = await getSupabaseClient().rpc('get_warehouse_subrentals_page', {
    ...commonArgs(params),
    p_kind: params.kind,
    p_window_from: dateOrNull(params.windowFrom),
    p_window_to: dateOrNull(params.windowTo),
    p_vendor_ids: params.vendorIds.length > 0 ? params.vendorIds : null,
  });
  if (error) throw error;
  return parsePage(data, parseSubrentalRow);
}

export async function fetchWarehouseTabCounts(locationIds: string[] | null): Promise<WarehouseTabCounts> {
  const { data, error } = await getSupabaseClient().rpc('get_warehouse_tab_counts', {
    p_location_ids: locationIds && locationIds.length > 0 ? locationIds : null,
  });
  if (error) throw error;
  const object = asObject(data) ?? {};
  return {
    transfers: asNullableNumber(object.transfers),
    requests: asNullableNumber(object.requests),
    purchaseOrders: asNullableNumber(object.purchase_orders),
    subrentals: asNullableNumber(object.subrentals),
  };
}

/** The viewer's IANA time zone, so "today" and "late" match their wall clock when a site has none. */
export function viewerTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
  } catch {
    return 'America/New_York';
  }
}

export const warehouseQueryKeys = {
  all: ['warehouse'] as const,
  tabCounts: (locationIds: string[] | null) => ['warehouse', 'tab-counts', locationIds ?? 'all'] as const,
  transfers: (params: WarehouseTransfersParams) => ['warehouse', 'transfers-page', params] as const,
  purchaseOrders: (params: WarehousePurchaseOrdersParams) => ['warehouse', 'purchase-orders-page', params] as const,
  subrentals: (params: WarehouseSubrentalsParams) => ['warehouse', 'subrentals-page', params] as const,
};

// The library envelope parser and its JSON readers, shared with the Inventory library
// (data/inventoryLibrary.ts), whose page functions return the same envelope.
export { asCounts, asNullableNumber, asNumber, asObject, asText, parsePage as parseLibraryPage };
export type { Json as LibraryJson };
